import { Table, Modal, DateInput } from "@/vendor/Forge/dist";
import { TaoList } from "@/types/tao-routing";
import { RowData } from "@/types/my-tasks";
import { styled } from "@stitches/react";
import {
  ComposeSmall,
  CopySmall,
  InfoButtonSmall,
} from "@/vendor/forge-icons/dist";
import { Button } from "@/vendor/Forge/dist";
import { ToggleSwitch } from "@/vendor/Forge/dist";
import TaoField from "../TaoField";
import { useTaoRulesContext } from "@/contexts/tao-rule-context";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUpdateTaoStatus } from "@/hooks/use-update-tao-status";
import { useUpdateTaoRenderStatus } from "@/hooks/use-update-tao-render-status";
import { DateInputOnChangeEvent } from "@/vendor/Forge/dist";

const Wrapper = styled("div", {
  padding: "0 16px",
  display: "flex",
  width: "100%",

  "& table": {
    width: "100%",
    height: "3em",
  },
  "& table th, & table td": {
    border: "1px solid #B6B7B7",
    textAlign: "left",
    verticalAlign: "top",
  },
  "& .fe_c_table.fe_c_table--compact tbody td.fe_c_table__cell": {
    padding: "8px 12px",
  },
  "& .fe_c_table.fe_c_table--compact thead th.fe_c_table__header": {
    padding: "12px 12px",
  },
  "& .fe_c_toggle-switch": {
    marginTop: "0px",
  },
  ".highlightDestinationBucket tbody tr:first-child td:nth-child(7)": {
    borderLeft: "5px solid Green",
  },
});

const RenderModalButtonRowWrapper = styled("div", {
  display: "flex",
  justifyContent: "right",
  gap: "5px",
  paddingTop: "23px",
  borderTop: "1px solid #B6B7B7",
});

const RenderModalContentWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  marginBottom: "16px",
  borderTop: "1px solid #B6B7B7",
});

const ActionWrapper = styled("div", {
  display: "flex",
});

const getLastThirtyDays = () => {
  const currentDate = new Date();
  const currentDateMilliseconds = currentDate.getTime();
  const thirtyDaysBeforeCurrentDateMil =
    currentDateMilliseconds - 30 * (1000 * 60 * 60 * 24);
  const thirtyDaysBeforeCurrentDate = new Date(thirtyDaysBeforeCurrentDateMil);

  return thirtyDaysBeforeCurrentDate;
};

interface Props {
  rows: TaoList[];
  setSelectedTaoId: (data: number) => void;
}

const AdminTaoRoutingTable = ({ rows, setSelectedTaoId }: Props) => {
  const { filters } = useTaoRulesContext();
  const [showBucketHighlight, setShowBucketHighlight] = useState(false);
  const [showRenderModal, setShowRenderModal] = useState(false);
  const [renderModalTaoId, setRenderModalTaoId] = useState<number>();
  const [renderModalTaoChecked, setRenderModalTaoChecked] = useState<boolean>();
  const [renderDateModal, setRenderDateModal] = useState<string>();
  const navigate = useNavigate();
  const { updateTaoStatus, isPending } = useUpdateTaoStatus();
  const { updateTaoRenderStatus, isPending: isRenderStatusPending } =
    useUpdateTaoRenderStatus();

  useEffect(() => {
    if (
      filters.departments[0] != undefined ||
      filters.orderGenusList[0] != undefined ||
      filters.orderTypes[0] != undefined ||
      filters.providers[0] != undefined
    ) {
      setShowBucketHighlight(true);
    } else {
      setShowBucketHighlight(false);
    }
  }, [filters]);

  useEffect(() => {
    setRenderDateModal(undefined);
  }, [showRenderModal]);

  const handleDuplicateTao = (taoId: number) => {
    const selectedTaoData = rows.filter((tao) => tao.taoId == taoId);
    navigate("/tao-routing/tao-editor?currentStep=departments", {
      state: { selectedTaoData: selectedTaoData, isDuplicate: true },
    });
  };

  const handleEditTao = (taoId: number) => {
    const selectedTaoData = rows.filter((tao) => tao.taoId == taoId);
    navigate("/tao-routing/tao-editor?currentStep=departments", {
      state: { selectedTaoData: selectedTaoData, isEdit: true },
    });
  };

  const handleClick = (taoId: number) => {
    setSelectedTaoId(taoId);
  };

  const columns = [
    {
      key: "taoId",
      displayName: "TAO ID",
    },
    {
      key: "departments",
      displayName: "Department",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <TaoField taoFieldData={rowData.departments} />
      ),
    },
    {
      key: "providers",
      displayName: "Providers",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <TaoField taoFieldData={rowData.providers} />
      ),
    },
    {
      key: "orderGenusList",
      displayName: "Genus",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <TaoField taoFieldData={rowData.orderGenusList} />
      ),
    },
    {
      key: "orderTypes",
      displayName: "Order Type",
    },
    {
      key: "ordering",
      displayName: "Priority",
    },
    {
      key: "bucketName",
      displayName: "Bucket",
    },
    {
      key: "actions",
      displayName: "Actions",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <ActionWrapper>
          <Button
            variant="tertiary"
            onClick={() => handleDuplicateTao(rowData.taoId)}
          >
            <CopySmall title="Duplicate TAO" />
          </Button>
          <Button
            variant="tertiary"
            onClick={() => handleEditTao(rowData.taoId)}
          >
            <ComposeSmall title="Edit TAO" />
          </Button>
          <Button variant="tertiary" onClick={() => handleClick(rowData.taoId)}>
            <InfoButtonSmall title="Show TAO History" />
          </Button>
        </ActionWrapper>
      ),
    },
    {
      key: "",
      displayName: "Active/Inactive",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <ToggleSwitch
          valueAttribute={rowData.taoId.toString()}
          disabled={isPending}
          checked={rowData.active}
          onChange={() => {
            updateTaoStatus(rowData.taoId, !rowData.active);
          }}
          descriptions={{
            id: "toggle",
            checked: "Active",
            unchecked: "Inactive",
          }}
        />
      ),
    },
    {
      key: "",
      displayName: "Display Orders",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <ToggleSwitch
          valueAttribute={rowData.taoId.toString()}
          disabled={isRenderStatusPending}
          checked={rowData.renderOnDashboard}
          onChange={() => {
            if (!rowData.renderOnDashboard) {
              setRenderModalTaoId(rowData.taoId);
              setRenderModalTaoChecked(rowData.renderOnDashboard);
              setShowRenderModal(true);
            } else {
              updateTaoRenderStatus(rowData.taoId, !rowData.renderOnDashboard);
            }
          }}
          descriptions={{
            id: "toggle",
            checked: "Display",
            unchecked: "Not Displayed",
          }}
        />
      ),
    },
  ];

  return (
    <Wrapper>
      <Table
        className={
          showBucketHighlight ? "highlightDestinationBucket" : undefined
        }
        rowKey="taoId"
        layout="compact"
        columns={columns}
        rows={rows}
      />
      <Modal
        includeButtonRow={false}
        headerText="Select a date for orders you want to display"
        show={showRenderModal}
        alertType="attention"
        onHide={() => {
          setShowRenderModal(false);
          setRenderModalTaoId(undefined);
          setRenderModalTaoChecked(undefined);
        }}
        onBackdropClick={() => {
          setShowRenderModal(false);
          setRenderModalTaoId(undefined);
          setRenderModalTaoChecked(undefined);
        }}
      >
        <RenderModalContentWrapper>
          <p>Date Selected must be within the last 30 days.</p>
          <DateInput
            minDate={new Date(getLastThirtyDays())}
            onChange={(e: DateInputOnChangeEvent) => {
              setRenderDateModal(
                new Date(e.target.value ?? new Date()).toLocaleDateString(
                  "en-US",
                  { month: "2-digit", day: "2-digit", year: "numeric" },
                ),
              );
            }}
          />
        </RenderModalContentWrapper>
        <RenderModalButtonRowWrapper>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRenderModal(false);
              setRenderModalTaoId(undefined);
              setRenderModalTaoChecked(undefined);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={renderDateModal === undefined}
            onClick={() => {
              if (
                typeof renderModalTaoId == "number" &&
                typeof renderModalTaoChecked == "boolean"
              )
                updateTaoRenderStatus(
                  renderModalTaoId,
                  !renderModalTaoChecked,
                  renderDateModal,
                );
            }}
          >
            Save
          </Button>
        </RenderModalButtonRowWrapper>
      </Modal>
    </Wrapper>
  );
};

const NonAdminTaoRoutingTable = ({ setSelectedTaoId, rows }: Props) => {
  const { filters } = useTaoRulesContext();
  const [showBucketHighlight, setShowBucketHighlight] = useState(false);

  const handleClick = (taoId: number) => {
    setSelectedTaoId(taoId);
  };

  useEffect(() => {
    if (
      filters.departments[0] != undefined ||
      filters.orderGenusList[0] != undefined ||
      filters.orderTypes[0] != undefined ||
      filters.providers[0] != undefined
    ) {
      setShowBucketHighlight(true);
    } else {
      setShowBucketHighlight(false);
    }
  }, [filters]);

  const columns = [
    {
      key: "taoId",
      displayName: "TAO ID",
    },
    {
      key: "departments",
      displayName: "Department",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <TaoField taoFieldData={rowData.departments} />
      ),
    },
    {
      key: "providers",
      displayName: "Providers",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <TaoField taoFieldData={rowData.providers} />
      ),
    },
    {
      key: "orderGenusList",
      displayName: "GENUS",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <TaoField taoFieldData={rowData.orderGenusList} />
      ),
    },
    {
      key: "orderTypes",
      displayName: "ORDER TYPE",
    },
    {
      key: "ordering",
      displayName: "PRIORITY",
    },
    {
      key: "bucketName",
      displayName: "BUCKET",
    },
    {
      key: "actions",
      displayName: "ACTIONS",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <ActionWrapper>
          <Button variant="tertiary" onClick={() => handleClick(rowData.taoId)}>
            <InfoButtonSmall title="Show TAO History" />
          </Button>
        </ActionWrapper>
      ),
    },
    {
      key: "",
      displayName: "ACTIVE/INACTIVE",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <ToggleSwitch
          valueAttribute={rowData.taoId.toString()}
          disabled={true}
          checked={rowData.active}
          descriptions={{
            id: "toggle",
            checked: "Active",
            unchecked: "Inactive",
          }}
        />
      ),
    },
    {
      key: "",
      displayName: "DISPLAY/HIDE",
      template: (_field: string, { rowData }: RowData<TaoList>) => (
        <ToggleSwitch
          valueAttribute={rowData.taoId.toString()}
          disabled={true}
          checked={rowData.renderOnDashboard}
          descriptions={{ id: "toggle", checked: "Display", unchecked: "Hide" }}
        />
      ),
    },
  ];

  return (
    <Wrapper>
      <Table
        className={
          showBucketHighlight ? "highlightDestinationBucket" : undefined
        }
        rowKey="taoId"
        layout="compact"
        columns={columns}
        rows={rows}
      />
    </Wrapper>
  );
};

export { AdminTaoRoutingTable, NonAdminTaoRoutingTable };
